import { inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AdStats, Currency, InventoryConfig } from "@front/m19-api-client";
import { AdStatsEx, OrderStats, ProductGroupEx } from "@front/m19-models";
import {
  AccountSelectionService,
  AsinService,
  CatalogBrandService,
  CurrencyService,
  InventoryService,
  ProductGroupService,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { DateAggregation } from "@front/m19-utils";
import { ProfitDetail } from "@m19-board/product360/product360.service";
import { DashboardConfigService } from "@m19-board/services/dashboard-config.service";
import { ProfitCategory, ProfitDetailsService, ProfitLine } from "@m19-board/services/profit-details.service";
import {
  hasProductTag1,
  hasProductTag2,
  hasProductTags,
  isBrandConfig,
  isProductGroupConfig,
  ProductTagFilterConfig,
  WidgetConfig,
} from "@m19-board/widget/widget-config";
import { WidgetType } from "@m19-board/widget/widget.model";
import { BehaviorSubject, combineLatest, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { FilterCategory } from "@m19-board/shared/compact-filter/compact-filter.component";

@Injectable()
export abstract class Service360 {
  protected readonly statsService = inject(StatsService);
  protected readonly accountSelection = inject(AccountSelectionService);
  protected readonly inventoryService = inject(InventoryService);
  protected readonly userSelectionService = inject(UserSelectionService);
  protected readonly asinService = inject(AsinService);
  private readonly dashboardConfigService = inject<DashboardConfigService<WidgetType>>(DashboardConfigService);
  private readonly productGroupService = inject(ProductGroupService);
  protected readonly catalogBrandService = inject(CatalogBrandService);
  protected readonly profitDetailsService = inject(ProfitDetailsService);
  protected readonly currencyService = inject(CurrencyService);

  readonly inventoryConfig$: Observable<InventoryConfig> =
    this.accountSelection.singleAccountMarketplaceSelection$.pipe(
      switchMap((am) => this.asinService.getInventoryConfig(am.accountId, am.marketplace)),
      shareReplay(1),
    );

  public readonly productGroups$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.productGroupService.getProductGroups(am.accountId, am.marketplace)),
    shareReplay(1),
  );
  protected productGroups = toSignal(this.productGroups$);

  public readonly productGroupIndex$ = this.productGroups$.pipe(
    map((productGroups) => {
      const productGroupIndex = new Map<number, ProductGroupEx>();
      for (const pg of productGroups) {
        productGroupIndex.set(pg.productGroupId, pg);
      }
      return productGroupIndex;
    }),
    startWith(new Map<number, ProductGroupEx>()),
  );

  public readonly catalog$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.asinService.getCatalog(am.accountId, am.marketplace)),
    shareReplay(1),
  );
  protected readonly catalogBrandsWithAsins$ = this.catalogBrandService.catalogBrandsWithAsins$;
  readonly customFieldMap$: Observable<
    Map<
      string,
      {
        customField1: string | undefined;
        customField2: string | undefined;
      }
    >
  > = this.catalog$.pipe(
    map((catalog) => {
      const customFieldMap = new Map<
        string,
        {
          customField1: string | undefined;
          customField2: string | undefined;
        }
      >();
      for (const product of catalog.products) {
        customFieldMap.set(product.asin!, {
          customField1: product.productTag1,
          customField2: product.productTag2,
        });
      }
      return customFieldMap;
    }),
    shareReplay(1),
  );

  dailyProductStats$!: Observable<AdStats[]>;
  productStatsReload$!: Observable<void>;
  previousDailyProductStats$!: Observable<AdStats[]>;
  aggregatedProductStats$!: Observable<AdStats>;
  aggregatedProductStatsPreviousPeriod$!: Observable<AdStats | undefined>;
  productStatsPerAsin$!: Observable<AdStatsEx[]>;
  previousProductStatsPerAsin$!: Observable<AdStatsEx[]>;
  profitDetails$!: Observable<ProfitDetail[]>;

  private hoveredLineChartIndexSubject = new BehaviorSubject<number | undefined>(undefined);
  public hoveredLineChartIndex$ = this.hoveredLineChartIndexSubject.asObservable();
  private dateAggregationSubject$ = new BehaviorSubject<DateAggregation>(DateAggregation.daily);
  public dateAggregation$: Observable<DateAggregation> = this.dateAggregationSubject$.asObservable();

  private displayProductEventAnnotationsSubject$ = new BehaviorSubject<boolean>(false);
  public displayProductEventAnnotations$: Observable<boolean> =
    this.displayProductEventAnnotationsSubject$.asObservable();

  selectedDashboardConfig = toObservable(this.dashboardConfigService.savedDashboardConfig);

  private globalFilters = signal<ProductTagFilterConfig>({});
  public globalFilters$ = toObservable(this.globalFilters);

  protected constructor() {
    this.selectedDashboardConfig.pipe(takeUntilDestroyed()).subscribe((c) => {
      this.globalFilters.set({
        customField1: c?.globalFilters?.customField1,
        customField2: c?.globalFilters?.customField2,
      });
    });
  }

  public hoverLineChart(index: number | undefined) {
    this.hoveredLineChartIndexSubject.next(index);
  }

  setDateAggregation(dateAggregation: DateAggregation) {
    this.dateAggregationSubject$.next(dateAggregation);
  }

  displayProductEventAnnotations(display: boolean) {
    this.displayProductEventAnnotationsSubject$.next(display);
  }

  setGlobalFilters(filters: ProductTagFilterConfig) {
    this.globalFilters.set(filters);
    this.dashboardConfigService.updateDashboardConfigWithGlobalFilters(filters);
  }

  // TODO(joseph|hugo|clément): we probably can optimize this and avoid running through the product groups every time
  getProductGroup(config: WidgetConfig<WidgetType>): Observable<ProductGroupEx | undefined> {
    if (!isProductGroupConfig(config)) return of(undefined);

    if (typeof config.productGroupId === "number") {
      return this.productGroups$.pipe(
        map((productGroups) => productGroups.find((pg) => pg.productGroupId === config.productGroupId)),
      );
    } else {
      return this.productGroups$.pipe(
        map((productGroups) =>
          productGroups.find((pg) => (config.productGroupId as number[]).includes(pg.productGroupId)),
        ),
      );
    }
  }

  filterStats<T extends AdStatsEx | OrderStats>(
    data$: Observable<T[]>,
    config: WidgetConfig<WidgetType>,
  ): Observable<T[]> {
    return combineLatest([
      data$,
      this.customFieldMap$,
      this.catalogBrandsWithAsins$,
      this.getProductGroup(config),
    ]).pipe(
      map(([data, customFieldMap, brands, productGroup]) =>
        this.applyStatsFilter(data, config, customFieldMap, brands, productGroup),
      ),
    );
  }

  getFilteredAsins(config: WidgetConfig<WidgetType>) {
    return combineLatest([
      this.catalog$,
      this.customFieldMap$,
      this.catalogBrandsWithAsins$,
      this.getProductGroup(config),
    ]).pipe(
      map(([catalog, customFieldMap, brands, productGroup]) =>
        this.applyStatsFilter(catalog.products, config, customFieldMap, brands, productGroup),
      ),
    );
  }

  private applyStatsFilter<T extends { asin?: string; brandId?: number }>(
    data: T[],
    config: WidgetConfig<WidgetType>,
    customFieldMap: Map<string, { customField1: string | undefined; customField2: string | undefined }>,
    brands: Map<number, Set<string>>,
    productGroup: ProductGroupEx | undefined,
  ): T[] {
    const customFieldFilters: ProductTagFilterConfig = hasProductTags(config) ? config : {};

    let brandsIds = isBrandConfig(config) ? config.brandIds : undefined;
    if (brands.size == brandsIds?.length) {
      // If all brands are selected, we can skip brand filtering
      brandsIds = undefined;
    }

    return data.filter((x) =>
      this.asinPassesFilter(productGroup, x, customFieldMap, customFieldFilters, brandsIds, brands),
    );
  }

  asinPassesFilter<T extends { asin?: string; brandId?: number }>(
    productGroup: ProductGroupEx | undefined,
    stat: T,
    customFieldMap: Map<
      string,
      {
        customField1: string | undefined;
        customField2: string | undefined;
      }
    >,
    customFieldFilters: ProductTagFilterConfig,
    brandConfig?: number[],
    brandsAsins?: Map<number, Set<string>>,
  ) {
    if (stat.asin === undefined) {
      return false;
    }
    const asinCustomField = customFieldMap.get(stat.asin) ?? { customField1: "", customField2: "" };
    return this.checkAsinFilter(
      productGroup,
      stat,
      customFieldFilters,
      brandConfig,
      brandsAsins,
      asinCustomField.customField1,
      asinCustomField.customField2,
    );
  }

  checkAsinFilter<T extends { asin?: string; brandId?: number }>(
    productGroup: ProductGroupEx | undefined,
    stat: T,
    customFieldFilters: ProductTagFilterConfig,
    brandFilters?: number[],
    brandsAsins?: Map<number, Set<string>>,
    customField1?: string,
    customField2?: string,
  ) {
    if (!stat.asin) {
      return false;
    }
    if (productGroup && !productGroup.items.includes(stat.asin!)) {
      return false;
    }

    if (
      hasProductTag1(customFieldFilters) &&
      (!customField1 || !customFieldFilters.customField1.includes(customField1))
    ) {
      return false;
    }

    if (
      hasProductTag2(customFieldFilters) &&
      (!customField2 || !customFieldFilters.customField2.includes(customField2))
    ) {
      return false;
    }

    if (brandFilters?.length) {
      if (stat.brandId) {
        return brandFilters.includes(stat.brandId);
      } else if (brandsAsins) {
        return this.brandFilterMatchesAsin(brandFilters, brandsAsins, stat.asin!);
      }
      return false;
    }

    return true;
  }

  private brandFilterMatchesAsin(brandFilters: number[], brandsAsins: Map<number, Set<string>>, asin: string) {
    return brandFilters.some((filter) => brandsAsins.get(filter)?.has(asin));
  }

  public buildProfitDetails(data: OrderStats | undefined, withRawTitle = false): ProfitDetail[] {
    const res: ProfitDetail[] = [];

    const profitLines: (ProfitLine | ProfitCategory)[] = this.profitDetailsService.getAllProfitLines(
      data!,
      "en-US",
      Currency.EUR,
    );

    const totalRevenue = profitLines.at(0)?.value ?? 0;
    for (const line of profitLines) {
      if (line.type === "category") {
        for (const categoryLine of line.lines) {
          if (categoryLine.type === "currency") {
            if (categoryLine.value != 0) {
              res.push({
                field: withRawTitle
                  ? categoryLine.title.replace("_title", "").replace("metrics.", "")
                  : `${categoryLine.title}`,
                category: withRawTitle ? categoryLine.title : `${line.title}`,
                value: categoryLine.value,
                aggValue: line.value,
                currency: Currency.EUR,
                totalRevenue: totalRevenue,
              });
            }
          }
        }
      } else {
        if (line.type === "currency") {
          res.push({
            field: line.title,
            value: line.value,
            category: line.title,
            currency: Currency.EUR,
            aggValue: line.value,
            totalRevenue: totalRevenue,
          });
        }
      }
    }
    return res;
  }
}

export type AvailableFilterFor360 = FilterCategory.ProductTag1 | FilterCategory.ProductTag2;
